<template>
  <div :key="id" class="desk-editor">
    <AppMapPointPanelHeader :header="header" @close="$emit('close')" />
    <div class="desk-editor__content">
      <div v-if="deskEditEnabled">
        <slot name="floor"></slot>
        <b-form-group label="Number" :state="!deskTempErrors.number">
          <b-input type="text" placeholder="Desk number" v-model="deskTemp.number" />
          <template #invalid-feedback v-if="deskTempErrors.number">
            {{ deskTempErrors.number }}
          </template>
        </b-form-group>
        <b-form-group label="Note">
          <b-textarea placeholder="Desk note" v-model="deskTemp.note" />
        </b-form-group>
        <div class="d-flex">
          <b-btn variant="primary" size="sm" @click="save" class="mr-2 ml-auto">Save</b-btn>
          <b-btn variant="default" size="sm" @click="cancelEdit">Cancel</b-btn>
        </div>
      </div>
      <div v-else>
        <div class="desk-editor__table">
          <span class="desk-editor__table-label">Number:</span>
          <span>{{ desk.number }}</span>
          <span class="desk-editor__table-label">Note:</span>
          <span class="multiline">{{ desk.note }}</span>
        </div>
        <div class="d-flex" v-if="canDeskEdit">
          <b-btn variant="secondary" size="sm" @click="deskEditEnabled = true" class="mt-2 ml-auto">Edit Desk</b-btn>
        </div>
      </div>

      <div class="desk-editor-top">
        <legend class="col-form-label pt-0 mt-4 mb-0">Bookings</legend>
      </div>
      <div class="timeline-wrapper">
        <div class="month-navigator">
          <div class="month-actions">
            <div :class="['month-panel-toggle', { opened: monthPanelEnabled }]">
              <a href="#" @click.prevent="monthPanelToggle">{{ monthLabel }}</a>
            </div>
            <div class="month-toggle-left" @click="subMonth"></div>
            <div class="month-toggle-right" @click="addMonth"></div>
          </div>
          <div class="month-panel" v-if="monthPanelEnabled">
            <AppCalendarTable :first-day-of-active-month="startDate" :marks-map="marksMap"
                              :marks-format="serverDateFormat" />
          </div>
        </div>
        <div class="timeline" v-if="deskEvents && deskEvents.length">
          <DeskEvent @edit="edit(event)" @addAfter="addEventAfter(i)" v-for="(event, i) in deskEvents" :key="event.id"
                     :event="event" />
        </div>
        <div v-else class="month-empty">
          No active bookings. <a href="#" v-if="canDeskEventCreate" @click.prevent="addEvent">Create new!</a>
        </div>
      </div>
      <DeskEventEditor
        @close="editClose"
        :event="editableEvent"
        v-if="editableEvent"
        :marks-map="marksMap"
        :marks-format="serverDateFormat"
      />
      <plus title="Book this Desk" class="plus" v-if="canDeskEventCreate" @click.native="addEvent" />
      <b-btn type="button" variant="default" size="sm" @click="destroy" title="Delete desk" v-if="!isNew"
             class="desk-destroy mr-2"
      ><i class="ion ion-md-trash"></i
      ></b-btn>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { createDeskEvent, SERVER_DATE_TIME_FORMAT, toBuildingDate } from './utils'
import Plus from './Plus'
import { isLocalId } from '@/utils/Id'
import { DESK_EVENT_CREATE } from '@/store/modules/desk'
import DeskEvent from './DeskEvent.vue'
import DeskEventEditor from './DeskEventEditor.vue'
import AppCalendarTable from '@/components/shared/AppCalendarTable'
import isEqual from 'lodash/isEqual'
import AppMapPointPanelHeader from '@/components/shared/AppMapPointPanelHeader.vue'
import { getDeskHeader } from '@/components/desk/helpers'
import * as PN from '@/utils/PermissionNames'

export default {
  name: 'DeskEditor',
  components: {
    AppMapPointPanelHeader,
    Plus,
    AppCalendarTable,
    DeskEventEditor,
    DeskEvent
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    point: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    editableEvent: null,
    // the Free Desks screen links here with a window to book; only honour it once
    bookingWindowApplied: false,
    timelineHeight: 0,
    startDate: dayjs().date(1),
    monthPanelEnabled: false,
    pointPanelEl: null,
    serverDateFormat: 'YYYY-MM-DD',
    deskEditEnabled: false,
    deskTemp: {},
    deskTempErrors: {},
    pointTemp: {}
  }),
  computed: {
    ...mapGetters('rbac', ['can']),
    ...mapGetters('desk', ['getDesk', 'getDeskEvent', 'deskEvents', 'getDeskTimezone']),
    header() {
      return this.desk ? getDeskHeader(this.desk) : ''
    },
    buildingTimezone() {
      return this.getDeskTimezone(this.id) || dayjs.tz.guess()
    },
    haveChanges() {
      return !isEqual(this.desk, this.deskTemp) || !isEqual(this.point, this.pointTemp)
    },
    marksMap() {
      const map = new Map()
      this.deskEvents.forEach((deskEvent) => {
        const startDate = deskEvent.start.split(' ')[0]
        const endDate = deskEvent.end.split(' ')[0]
        map.set(startDate, true)
        if (startDate !== endDate) {
          let startObject = dayjs(startDate, this.serverDateFormat)
          let endObject = dayjs(endDate, this.serverDateFormat)
          let diff = endObject.diff(startObject, 'day')
          for (let i = 0; i < diff; i++) {
            startObject = startObject.add(1, 'day')
            map.set(startObject.format(this.serverDateFormat), true)
          }
        }
      })
      return map
    },
    canDeskEdit() {
      return this.can(PN.DESK_UPDATE)
    },
    canDeskEventCreate() {
      return this.can(PN.DESK_EVENT_CREATE)
    },
    monthLabel() {
      return this.startDate.format('MMMM YYYY')
    },
    desk() {
      return this.getDesk(this.id) || {}
    },
    isNew() {
      return isLocalId(this.id)
    },
    dates() {
      const dates = []
      let date = this.startDate.subtract(this.startDate.day(), 'day')
      for (let i = 0; i < 42; i++) {
        dates.push(date)
        date = date.add(1, 'day')
      }
      return dates
    }
  },
  methods: {
    ...mapActions('desk', ['deskSave', 'deskLoad', 'deskEventsLoad', 'deskDestroy']),
    ...mapActions('point', ['pointSave', 'pointLoad']),
    ...mapMutations('desk', {
      createEvent: DESK_EVENT_CREATE
    }),
    destroy() {
      this.$appToast.question('A you sure want to delete desk?').then(() => {
        this.$emit('destroy')
      })
    },
    subMonth() {
      this.startDate = this.startDate.subtract(1, 'month')
      this.load()
    },
    addMonth() {
      this.startDate = this.startDate.add(1, 'month')
      this.load()
    },
    monthPanelToggle() {
      this.monthPanelEnabled = !this.monthPanelEnabled
    },
    edit(event) {
      this.editableEvent = event
    },
    editClose() {
      this.editableEvent = null
      this.load()
    },
    getPointPanelEl() {
      if (!this.pointPanelEl) {
        const el = document.getElementById('point-panel')
        if (!el) throw new Error('Point panel element not found.')
        this.pointPanelEl = el
      }
      return this.pointPanelEl
    },
    async save() {
      try {
        this.deskTempErrors = {}
        const pointId = await this.pointSave({ id: this.point.id, lat_lon: this.point.lat_lon })
        this.pointTemp = { ...this.point }

        const deskPromise = await this.deskSave({ desk: { ...this.deskTemp, id: this.id, point_id: pointId } })
        this.deskTemp = { ...this.desk }

        // reload to get new title
        await this.pointLoad({ id: pointId })

        this.$emit('save', { pointId })

        return deskPromise
      } catch (e) {
        this.onServerError('deskTemp', e)
      }
    },
    async cancelEdit() {
      this.load()
      this.point.lat_lon = this.pointTemp.lat_lon
      this.deskEditEnabled = false
    },
    onServerError(targetField, error) {
      if (error?.response) {
        this.$set(this, targetField + 'Errors', {})
        error.response.data.forEach(({ field, message }) => {
          this[targetField + 'Errors'][field] = message
        })
      } else {
        throw error
      }
    },
    /**
     * Open a new booking prefilled from the ?bookFrom/?bookTo the Free Desks list links with.
     * Guarded on the point id so the window lands on the desk the user actually picked.
     */
    applyLinkedBookingWindow() {
      if (this.bookingWindowApplied) return

      const { pointId, bookFrom, bookTo } = this.$route.query || {}
      if (!bookFrom || !bookTo || String(pointId) !== String(this.point.id)) return

      this.bookingWindowApplied = true
      this.addEvent({ start: bookFrom, end: bookTo })
    },
    addEvent(dates = null) {
      let event = createDeskEvent()
      event.desk_id = this.id
      if (dates) {
        event = Object.assign(event, dates)
      }
      this.editableEvent = event
    },
    addEventAfter(num) {
      let dates = {}
      dates.start = dayjs(this.deskEvents[num].end, SERVER_DATE_TIME_FORMAT).add(1, 'minute')

      if (this.deskEvents.length > num + 1) {
        dates.end = dayjs(this.deskEvents[num].start, SERVER_DATE_TIME_FORMAT)
        if (dates.start.isBefore(dates.end)) {
          dates.end = dates.end.subtract(1, 'minute')
        }
      } else {
        dates.end = dates.start.add(8, 'hour')
      }

      dates = {
        start: dates.start.format(SERVER_DATE_TIME_FORMAT),
        end: dates.end.format(SERVER_DATE_TIME_FORMAT)
      }
      this.addEvent(dates)
    },
    async load() {
      if (this.id) {
        await this.deskLoad({
          id: this.id,
          params: { expand: 'point' }
        })
        this.deskTemp = { ...this.desk }
        this.pointTemp = { ...this.point }

        // day boundaries of the calendar window must be building-local: a viewer browsing from a
        // different timezone than the desk's building would otherwise clip events near month edges
        const rangeStart = toBuildingDate(this.dates[0], this.buildingTimezone)
        const rangeEnd = toBuildingDate(this.dates[this.dates.length - 1], this.buildingTimezone)

        await this.deskEventsLoad({
          params: {
            expand: 'employee',
            filters: {
              desk: this.desk.id,
              since: rangeStart
                .hour(0)
                .minute(0)
                .second(0)
                .format(SERVER_DATE_TIME_FORMAT),
              until: rangeEnd
                .hour(23)
                .minute(59)
                .second(59)
                .format(SERVER_DATE_TIME_FORMAT)
            },
            pagerDisabled: true,
            sort: 'start'
          }
        })

        this.applyLinkedBookingWindow()
      }
    }
  },
  mounted() {
    this.pointTemp = { ...this.point }
    this.deskTemp = { ...this.desk }
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        if (this.isNew) {
          this.pointTemp = { ...this.point }
          this.deskTemp = { ...this.desk }
          this.save() // no need load new, it's empty
        } else {
          this.load()
        }
      }
    },
    'point.lat_lon': {
      handler() {
        this.deskEditEnabled = true
      }
    },
    editableEvent(value) {
      const panelEl = this.getPointPanelEl()
      if (value) {
        panelEl.classList.add('modal-enabled')
      } else {
        panelEl.classList.remove('modal-enabled')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.note {
  cursor: pointer;

  &:empty:after {
    content: 'add note';
  }
}

.desk-editor {
  &__table {
    display: grid;
    grid-template-columns: min-content auto;
    gap: 10px;

    &-label {
      font-weight: 500;
    }
  }

  &__content {
    padding: 24px;
  }

  .plus {
    position: fixed;
    bottom: 24px;
    right: 27px;
  }

  .desk-destroy {
    position: fixed;
    bottom: 30px;
    padding: 3px 14px;
    font-size: 18px;
    background: #fff;
    box-shadow: rgba(0, 0, 0, 0.24) 0 3px 8px;
  }

  .timeline-wrapper {
    margin: 0 -24px -24px;

    .timeline {
      padding: 8px 24px 24px;

      &:after {
        content: ' ';
        height: 20px;
        width: 1px;
        display: block;
      }
    }
  }

  .month-navigator {
    background: #ffffff;
    position: sticky;
    z-index: 1;
    top: 0;
  }

  .month-actions {
    padding: 8px 24px;
    display: flex;
    align-items: center;
  }

  .month-panel-toggle {
    &.opened > a:after {
      transform: rotate(-135deg);
    }

    & > a {
      font-size: 18px;
      white-space: nowrap;
      cursor: pointer;

      &:after {
        content: '';
        display: inline-block;
        width: 10px;
        height: 10px;
        margin: 2px 0 3px 8px;
        border-bottom: 1px solid #1e70cd;
        border-right: 1px solid #1e70cd;
        transform: rotate(45deg);
        transform-origin: 66% 66%;
        font-size: 22px;
        transition: transform 0.2s ease-in;
      }
    }
  }

  .month-toggle-left,
  .month-toggle-right {
    width: 10px;
    height: 10px;
    border-bottom: 2px solid #1e70cd;
    border-right: 2px solid #1e70cd;
    transform: rotate(-45deg);
    cursor: pointer;
  }

  .month-toggle-left {
    margin: 0 18px 0 auto;
    transform: rotate(135deg);
  }

  .month-toggle-right {
    margin-right: 2px;
  }

  .month-panel {
    padding: 12px 24px;
    box-shadow: 0 10px 7px -8px inset #ccc, 0px -10px 7px -8px inset #ccc;
    display: flex;
    justify-content: center;
  }

  .month-empty {
    text-align: center;
    padding: 24px;
  }
}
</style>
